/**
 * scorebookParser — reconstructs GameChanger scorebook play-by-play from the PDF
 * text layer (no OCR). Pure: takes PdfPage[] (from pdfText.loadPdfPages or a Node
 * calibration harness) and returns structured at-bats for both team pages.
 *
 * Layout learned from real KC Cannon Ballers exports (Jun 6–7 2026):
 *  - Header row: "<Team> <Home|Away> Date: YYYY/MM/DD Time: ...".
 *  - A horizontal inning header row of numbers; a repeated number (e.g. "1 1 2…")
 *    means that inning batted around — each time-through-the-order is its own column.
 *  - Left column = batting order (slot, jersey, name, positions).
 *  - Each diamond cell: "#<jersey> <code>", a ball row "B <pitch#s>" and strike row
 *    "S <pitch#s>" (the numbers are which pitch was a ball/strike → exact sequence),
 *    plus the verbose result in the diamond ("F8", "G4-3", "BB SB", "1B+E").
 *
 * Inning = column (by x); player = row (by y). Chronological order + leadoff come
 * from walking columns left→right through the batting order.
 *
 * Coordinate thresholds (ROW_TOL, COLUMN_MATCH_FRAC) are calibrated against the real
 * PDFs via scripts/inspect-scorebook.ts.
 */

import { PdfPage, PdfTextItem, groupIntoRows } from './types';
import {
  ParsedScorebook, ParsedTeamPage, ParsedAtBat, ParsedLineupEntry, PageSide, ParseConfidence,
} from '../../types/import.types';

// ── Tunables (calibrated against real exports) ────────────────────────────────
const ROW_TOL = 4;                 // y tolerance when grouping a cell's text into rows
const NAME_BAND_PAD = 6;           // pad above a player's name when forming their row band

// ── Result notation ───────────────────────────────────────────────────────────
const POSITION_BY_NUMBER: Record<string, string> = {
  '1': 'P', '2': 'C', '3': '1B', '4': '2B', '5': '3B', '6': 'SS', '7': 'LF', '8': 'CF', '9': 'RF',
};

interface ResultMeaning {
  resultCode: string;
  description: string;
  reachedBase: boolean;
  rbi: number;
}

/** Classify a verbose result token ("F8", "G4-3", "K2-3", "1B+E", "SF8", "BB"). */
function classifyResult(primary: string): ResultMeaning {
  const t = primary.trim();
  const fielders = (t.match(/\d/g) || []).map(d => POSITION_BY_NUMBER[d]).filter(Boolean);
  const toWhom = fielders.length ? ` to ${fielders[fielders.length - 1]}` : '';

  if (/^Kd/i.test(t)) return { resultCode: 'K', description: 'Strikeout (dropped 3rd strike)', reachedBase: false, rbi: 0 };
  if (/^K/i.test(t))  return { resultCode: 'K', description: 'Strikeout', reachedBase: false, rbi: 0 };
  if (/^BB/i.test(t)) return { resultCode: 'BB', description: 'Walk', reachedBase: true, rbi: 0 };
  if (/^HBP/i.test(t)) return { resultCode: 'HBP', description: 'Hit by pitch', reachedBase: true, rbi: 0 };
  if (/^HR/i.test(t)) return { resultCode: 'HR', description: 'Home run', reachedBase: true, rbi: 1 };
  if (/^3B/i.test(t)) return { resultCode: '3B', description: 'Triple', reachedBase: true, rbi: 0 };
  if (/^2B/i.test(t)) return { resultCode: '2B', description: 'Double', reachedBase: true, rbi: 0 };
  if (/^1B/i.test(t)) return { resultCode: '1B', description: t.includes('+E') ? 'Single + error' : 'Single', reachedBase: true, rbi: 0 };
  if (/^SF/i.test(t)) return { resultCode: 'SF', description: `Sacrifice fly${toWhom}`, reachedBase: false, rbi: 1 };
  if (/^SAC/i.test(t) || /^SH/i.test(t)) return { resultCode: 'SAC', description: 'Sacrifice bunt', reachedBase: false, rbi: 0 };
  if (/^DP/i.test(t)) return { resultCode: 'DP', description: 'Double play', reachedBase: false, rbi: 0 };
  if (/^FC/i.test(t)) return { resultCode: 'FC', description: "Fielder's choice", reachedBase: !t.includes('+E') ? true : true, rbi: 0 };
  if (/^F/i.test(t))  return { resultCode: 'F', description: `Flyout${toWhom}`, reachedBase: false, rbi: 0 };
  if (/^L/i.test(t))  return { resultCode: 'L', description: `Lineout${toWhom}`, reachedBase: false, rbi: 0 };
  if (/^G/i.test(t))  return { resultCode: 'G', description: `Groundout${toWhom}`, reachedBase: false, rbi: 0 };
  if (/^PO/i.test(t)) return { resultCode: 'PO', description: 'Putout', reachedBase: false, rbi: 0 };
  if (/^E/i.test(t))  return { resultCode: 'E', description: 'Reached on error', reachedBase: true, rbi: 0 };
  return { resultCode: t.slice(0, 3).toUpperCase() || '?', description: t || 'Unknown', reachedBase: false, rbi: 0 };
}

const BASERUNNING_TOKENS = new Set(['SB', 'WP', 'PB', 'DI', 'BK', 'TU', 'PO', 'CS']);
const isJersey = (s: string) => /^#\d+$/.test(s);
const isInt = (s: string) => /^\d{1,2}$/.test(s);

// ── Public API ────────────────────────────────────────────────────────────────

export function parseScorebook(pages: PdfPage[]): ParsedScorebook {
  const warnings: string[] = [];
  const parsed = pages.map(p => parseTeamPage(p, warnings));

  // Cross-attach the pitcher each batter faced: a page's pitcher is the OTHER
  // team's pitcher for that inning (symmetric; us/opp resolved at reconcile).
  //
  // Pair by SIDE, never by page count. A team batting more than 11 hitters (an
  // EH or continuous order, i.e. most of youth baseball) overflows onto a THIRD
  // lineup page, and the old `parsed.length === 2` guard then skipped attribution
  // for the WHOLE game: every at-bat lost its pitcher, and the import screen
  // showed a single pitcher named "Unknown" with a nonsense pitch count.
  const pitchersForSide = (side: PageSide) => {
    const merged = new Map<number, PitcherRef[]>();
    for (const p of parsed) {
      if (p.page.side !== side) continue;
      for (const [inn, refs] of p.pitcherByInning) {
        const existing = merged.get(inn);
        if (!existing) { merged.set(inn, [...refs]); continue; }
        // Overflow pages repeat the roster, so dedupe by jersey rather than
        // appending a phantom mid-inning change.
        for (const r of refs) if (!existing.some(e => e.number === r.number)) existing.push(r);
      }
    }
    return merged;
  };

  const awayPitchers = pitchersForSide('away');
  const homePitchers = pitchersForSide('home');
  for (const p of parsed) {
    attachPitchers(p.page, p.page.side === 'away' ? homePitchers : awayPitchers);
  }

  const pagesOut = parsed.map(p => p.page);
  return {
    source: 'scorebookPdf',
    date: parsed.find(p => p.date)?.date,
    awayTeam: pagesOut.find(p => p.side === 'away')?.teamName,
    homeTeam: pagesOut.find(p => p.side === 'home')?.teamName,
    pages: pagesOut,
    parseWarnings: warnings,
  };
}

interface PitcherRef { name: string; number: string }
interface ParsedPageResult {
  page: ParsedTeamPage;
  date?: string;
  pitcherByInning: Map<number, PitcherRef[]>;
}

/** Assign pitcher names to each at-bat in page.atBats.
 *  For single-pitcher innings, all at-bats get that pitcher.
 *  For mid-inning changes (two pitchers in the list), the at-bats are split in half
 *  chronologically — starter gets the first ⌈n/2⌉, reliever gets the rest.
 *  This is a best-effort estimate when no Stats Sheet BF data is available; when a
 *  Stats Sheet IS attached, applyStatsSheet in reconcileGameImport.ts overrides these
 *  values with exact BF-based attribution. */
function attachPitchers(page: ParsedTeamPage, pitcherByInning: Map<number, PitcherRef[]>) {
  // Group at-bats by inning (preserving parse order = chronological within each inning).
  const byInning = new Map<number, ParsedAtBat[]>();
  for (const ab of page.atBats) {
    (byInning.get(ab.inning) ?? byInning.set(ab.inning, []).get(ab.inning)!).push(ab);
  }

  for (const [inning, abs] of byInning) {
    const pitchers = pitcherByInning.get(inning);
    if (!pitchers || pitchers.length === 0) continue;

    if (pitchers.length === 1) {
      // Simple case: one pitcher owns the whole inning.
      for (const ab of abs) { ab.pitcherName = pitchers[0].name; ab.pitcherNumber = pitchers[0].number; }
    } else {
      // Mid-inning change: distribute chronologically. Starter gets the first half,
      // reliever(s) get the remainder. For 3+ pitchers in one inning, divide evenly.
      const chunkSize = Math.ceil(abs.length / pitchers.length);
      abs.forEach((ab, idx) => {
        const pitcherIdx = Math.min(Math.floor(idx / chunkSize), pitchers.length - 1);
        ab.pitcherName = pitchers[pitcherIdx].name;
        ab.pitcherNumber = pitchers[pitcherIdx].number;
      });
    }
  }
}

// ── Per-page parsing ──────────────────────────────────────────────────────────

function parseTeamPage(page: PdfPage, warnings: string[]): ParsedPageResult {
  const rows = groupIntoRows(page.items, ROW_TOL);

  const { teamName, side, date } = parseHeader(rows);
  const inningColumns = findInningColumns(rows, page.width);
  const spacing = inningColumns.length > 1 ? Math.abs(inningColumns[1].x - inningColumns[0].x) : 40;
  const gridLeft = (inningColumns[0]?.x ?? page.width * 0.25) - spacing / 2;
  const lineup = parseLineup(rows, gridLeft);
  const pitcherByInning = buildPitcherByInning(page.items, lineup, gridLeft);

  if (!inningColumns.length || !lineup.length) {
    warnings.push(`Page ${page.pageNumber} (${teamName || '?'}): could not locate ${!inningColumns.length ? 'inning columns' : 'lineup'}, so the grid was not parsed.`);
    return { page: { teamName, side, lineup, atBats: [] }, date, pitcherByInning };
  }

  const cells = bucketCells(page.items, lineup, inningColumns, warnings);
  const atBats = walkChronological(cells, lineup, inningColumns, side, warnings);

  return { page: { teamName, side, lineup, atBats }, date, pitcherByInning };
}

function parseHeader(rows: PdfTextItem[][]): { teamName: string; side: PageSide; date?: string } {
  // The header line is among the first rows and contains "Away"/"Home" + "Date:".
  let side: PageSide = 'home';
  let teamName = '';
  let date: string | undefined;

  for (const row of rows.slice(0, 6)) {
    const text = row.map(i => i.str).join(' ');
    const dm = text.match(/(\d{4})\/(\d{2})\/(\d{2})/);
    if (dm && !date) date = `${dm[1]}-${dm[2]}-${dm[3]}`;

    const sideIdx = row.findIndex(i => i.str.trim() === 'Away' || i.str.trim() === 'Home');
    if (sideIdx >= 0 && !teamName) {
      side = row[sideIdx].str.trim() === 'Away' ? 'away' : 'home';
      teamName = row.slice(0, sideIdx).map(i => i.str).join(' ').replace(/\s+/g, ' ').trim();
    }
  }
  // Fallback team name: the large title row (first row) if the above missed it.
  if (!teamName && rows[0]) teamName = rows[0].map(i => i.str).join(' ').replace(/\b(Away|Home|Date:.*)\b.*/i, '').trim();
  return { teamName, side, date };
}

/** The inning header is the widest horizontal run of integer items. Repeated
 *  numbers (batted-around) are preserved as separate columns. */
function findInningColumns(rows: PdfTextItem[][], pageWidth: number): Array<{ x: number; inning: number }> {
  let best: PdfTextItem[] = [];
  for (const row of rows) {
    const ints = row.filter(i => isInt(i.str) && Number(i.str) >= 1 && Number(i.str) <= 9);
    if (ints.length < 5) continue;
    const span = ints[ints.length - 1].x - ints[0].x;
    if (span > pageWidth * 0.5 && ints.length >= best.length) best = ints;
  }
  return best.map(i => ({ x: i.x, inning: Number(i.str) }));
}

const POS_RE = /^(P|C|1B|2B|3B|SS|LF|CF|RF|EH|DH|DP|FLEX)$/;
const CHANGE_COL_MIN = 118;  // primary position sits ~x107; the inn/pos change columns start ~x119

function parseLineup(rows: PdfTextItem[][], gridLeft: number): ParsedLineupEntry[] {
  const lineup: ParsedLineupEntry[] = [];
  const seen = new Set<string>();
  let slot = 0;
  for (const row of rows) {
    // Roster region only; drop the big faded slot numbers (height ~30 vs ~7).
    const left = row.filter(i => i.x < gridLeft && i.height <= 20);
    const jersey = left.find(i => i.x < 32 && isInt(i.str));
    if (!jersey) continue;
    const nameTokens = left.filter(i => i.x >= 32 && i.x < 95 && /[A-Za-z]/.test(i.str)).map(i => i.str.trim());
    const name = nameTokens.join(' ').replace(/\s+/g, ' ').trim();
    if (!name) continue;

    const number = jersey.str.trim();
    if (seen.has(number)) continue;     // dedup by jersey → drops GC's position-change reprints
    seen.add(number);

    // Primary position = the pos token on the name row (left of the change columns).
    const primaryPos = left
      .filter(i => i.x < CHANGE_COL_MIN && POS_RE.test(i.str.trim()))
      .sort((a, b) => a.x - b.x)[0];

    slot += 1;
    const entry: ParsedLineupEntry = { slot, number, name, positions: primaryPos ? [primaryPos.str.trim()] : [] };
    (entry as any)._y = jersey.y;
    lineup.push(entry);
  }
  return lineup;
}

/** inning → ordered list of pitchers (starter first, then any mid-inning relief in order).
 *  Multiple pitchers per inning = mid-inning change. The list order is entry order so
 *  attachPitchers can distribute at-bats chronologically within a shared inning. */
function buildPitcherByInning(items: PdfTextItem[], lineup: ParsedLineupEntry[], gridLeft: number): Map<number, PitcherRef[]> {
  const map = new Map<number, PitcherRef[]>();
  if (!lineup.length) return map;

  const anchors = lineup
    .map(l => ({ y: (l as any)._y as number, ref: { name: l.name, number: l.number } as PitcherRef }))
    .sort((a, b) => a.y - b.y);

  type Ev = { inning: number; isPrimary: boolean; y: number; ref: PitcherRef };
  const events: Ev[] = [];

  // Starters: primary position === 'P' → in from inning 1.
  for (const l of lineup) {
    if ((l.positions || [])[0] === 'P') events.push({ inning: 1, isPrimary: true, y: (l as any)._y, ref: { name: l.name, number: l.number } });
  }

  // Relief: 'P' tokens in the change columns; inning = nearest change-column int.
  const changeInts = items.filter(i => i.x >= CHANGE_COL_MIN && i.x < gridLeft && i.height <= 20 && /^[1-9]$/.test(i.str.trim()));
  const changePs = items.filter(i => i.x >= CHANGE_COL_MIN && i.x < gridLeft && i.height <= 20 && i.str.trim() === 'P');
  for (const p of changePs) {
    let inn = 0, bestD = 13;
    for (const ci of changeInts) { const d = Math.abs(ci.y - p.y); if (d < bestD) { bestD = d; inn = Number(ci.str); } }
    if (!inn) continue;
    let ref: PitcherRef | null = null, bd = 22;
    for (const a of anchors) { const d = Math.abs(a.y - p.y); if (d < bd) { bd = d; ref = a.ref; } }
    if (ref) events.push({ inning: inn, isPrimary: false, y: p.y, ref });
  }

  // Deduplicate: GC prints a second roster row for position-change players.
  const seen = new Set<string>();
  const dedupedEvents = events.filter(e => {
    const key = `${e.inning}|${e.ref.number}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Sort: primary entries first within each inning, then by inning.
  dedupedEvents.sort((a, b) => a.inning - b.inning || (a.isPrimary ? 0 : 1) - (b.isPrimary ? 0 : 1) || a.y - b.y);

  // Build explicit inning→pitcher(s) list. When a reliever's listed inning is already
  // occupied, they entered MID-INNING — append them to that inning's list rather than
  // bumping to the next inning (which was the old behavior that lost their at-bats).
  const explicit = new Map<number, PitcherRef[]>();
  for (const e of dedupedEvents) {
    const existing = explicit.get(e.inning);
    if (!existing) {
      explicit.set(e.inning, [e.ref]);
    } else {
      // Mid-inning entry: only add if not already in the list (dedup by jersey).
      if (!existing.some(r => r.number === e.ref.number)) existing.push(e.ref);
    }
  }

  // Carry forward: for each inning, use the last known pitcher list (single-entry
  // list = whole inning, multi-entry list = split inning tracked by attachPitchers).
  let cur: PitcherRef[] | undefined;
  for (let inn = 1; inn <= 9; inn++) {
    if (explicit.has(inn)) cur = explicit.get(inn);
    if (cur) map.set(inn, cur);
  }
  return map;
}

interface RawCell { playerIdx: number; colIdx: number; items: PdfTextItem[] }

function bucketCells(
  items: PdfTextItem[],
  lineup: ParsedLineupEntry[],
  columns: Array<{ x: number; inning: number }>,
  warnings: string[],
): Map<string, RawCell> {
  const bands = lineup.map((l, i) => ({
    idx: i,
    yTop: ((l as any)._y ?? 0) - NAME_BAND_PAD,
    yBottom: i + 1 < lineup.length ? ((lineup[i + 1] as any)._y ?? Infinity) - NAME_BAND_PAD : Infinity,
  }));
  const colXs = columns.map(c => c.x);
  const gridLeft = colXs[0] - (colXs[1] - colXs[0]) / 2;

  const cells = new Map<string, RawCell>();
  for (const it of items) {
    if (it.x < gridLeft) continue;                                  // roster / left margin
    const band = bands.find(b => it.y >= b.yTop && it.y < b.yBottom);
    if (!band) continue;
    const colIdx = nearestColumn(it.x, colXs);
    if (colIdx < 0) continue;
    const key = `${band.idx}:${colIdx}`;
    let cell = cells.get(key);
    if (!cell) { cell = { playerIdx: band.idx, colIdx, items: [] }; cells.set(key, cell); }
    cell.items.push(it);
  }
  return cells;
}

function nearestColumn(x: number, colXs: number[]): number {
  let best = -1, bestD = Infinity;
  for (let i = 0; i < colXs.length; i++) {
    const d = Math.abs(x - colXs[i]);
    if (d < bestD) { bestD = d; best = i; }
  }
  // half the inter-column spacing as the max match distance
  const spacing = colXs.length > 1 ? Math.abs(colXs[1] - colXs[0]) : Infinity;
  return bestD <= spacing * 0.6 ? best : -1;
}

/** Parse one cell's items into the content of an at-bat (no inning/leadoff yet). */
function parseCell(cell: RawCell): Omit<ParsedAtBat, 'inning' | 'isLeadoff' | 'timeThroughOrder' | 'batterName' | 'batterNumber' | 'batterSide' | 'lineupSlot'> | null {
  const rows = groupIntoRows(cell.items, ROW_TOL);
  const flat = cell.items.map(i => i.str.trim());

  // B / S pitch rows → exact sequence
  const balls = pitchIndices(rows, 'B');
  const strikes = pitchIndices(rows, 'S');
  const total = Math.max(0, ...balls, ...strikes);
  const pitchSequence: string[] = [];
  for (let i = 1; i <= total; i++) pitchSequence.push(strikes.includes(i) ? 'S' : 'B');

  // Result: prefer the verbose diamond token (has a fielder digit), else the short code.
  const codeTokens = flat.filter(s => s && !isJersey(s) && s !== 'B' && s !== 'S' && !isInt(s));
  const verbose = codeTokens.find(s => /^[A-Z].*\d/.test(s) || /^[0-9]?[A-Z]+\d/.test(s));
  const shortCode = codeTokens.find(s => /^(K|Kd\d?|BB|HBP|HR|3B|2B|1B|SF|SAC|DP|FC|F|L|G|PO|E)$/i.test(s));
  const primary = verbose || shortCode;
  if (!primary && pitchSequence.length === 0) return null; // empty template cell

  const baserunning = codeTokens.filter(s => BASERUNNING_TOKENS.has(s));

  let meaning: ResultMeaning;
  let confidence: ParseConfidence;
  if (primary) {
    meaning = classifyResult(primary);
    confidence = meaning.resultCode === '?' ? 'low' : 'high';
  } else if (strikes.length >= 2 && pitchSequence[pitchSequence.length - 1] === 'S') {
    // No code extracted, but the at-bat ended on a strike with 2+ strikes — the
    // GameChanger "backwards-K" (strikeout looking) glyph has no text layer.
    meaning = { resultCode: 'K', description: 'Strikeout (looking)', reachedBase: false, rbi: 0 };
    confidence = 'medium';
  } else {
    meaning = { resultCode: '?', description: 'Unparsed', reachedBase: false, rbi: 0 };
    confidence = 'low';
  }
  const resultText = primary || (meaning.resultCode !== '?' ? meaning.resultCode : '');

  return {
    pitcherTeam: 'opp',          // page-symmetric placeholder; reconcile maps us/opp
    result: [resultText, ...baserunning].filter(Boolean).join(' ').trim() || '?',
    resultCode: meaning.resultCode,
    resultDescription: meaning.description,
    reachedBase: meaning.reachedBase,
    rbi: meaning.rbi,
    runnersScored: 0,            // run attribution is a later sprint
    baseOutStateAtEntry: undefined,
    pitchSequence,
    balls: balls.length,
    strikes: strikes.length,
    pitchCount: pitchSequence.length,
    confidence,
    rawCell: confidence === 'low' ? flat.join(' ') : undefined,
  } as any;
}

function pitchIndices(rows: PdfTextItem[][], label: 'B' | 'S'): number[] {
  // A pitch row begins with the label and is followed by the pitch-number positions.
  for (const row of rows) {
    if (row[0]?.str.trim() === label) {
      return row.slice(1).map(i => Number(i.str)).filter(n => Number.isInteger(n) && n >= 1 && n <= 20);
    }
  }
  return [];
}

/** Walk columns left→right through the batting order to assign inning, leadoff and
 *  time-through-order. Repeated-inning columns (batted-around) continue the walk. */
function walkChronological(
  cells: Map<string, RawCell>,
  lineup: ParsedLineupEntry[],
  columns: Array<{ x: number; inning: number }>,
  side: PageSide,
  warnings: string[],
): ParsedAtBat[] {
  const N = lineup.length;
  const atBats: ParsedAtBat[] = [];
  const timesThrough = new Array(N).fill(0);
  let nextSlot = 0;
  let prevInning = -1;

  for (let colIdx = 0; colIdx < columns.length; colIdx++) {
    const inning = columns[colIdx].inning;
    const present = new Set<number>();
    for (let p = 0; p < N; p++) if (cells.has(`${p}:${colIdx}`)) present.add(p);
    if (!present.size) { prevInning = inning; continue; }

    let assignedFirstOfInning = inning !== prevInning;
    let guard = 0;
    while (present.size && guard++ < N * 2) {
      const p = nextSlot % N;
      if (present.has(p)) {
        const content = parseCell(cells.get(`${p}:${colIdx}`)!);
        present.delete(p);
        if (content) {
          timesThrough[p] += 1;
          atBats.push({
            ...(content as any),
            batterName: lineup[p].name,
            batterNumber: lineup[p].number,
            batterSide: side,
            lineupSlot: lineup[p].slot,
            inning,
            isLeadoff: assignedFirstOfInning,
            timeThroughOrder: timesThrough[p],
          });
          assignedFirstOfInning = false;
        }
      }
      nextSlot += 1;
    }
    if (guard >= N * 2 && present.size) {
      warnings.push(`Inning ${inning}: ${present.size} at-bat(s) did not fit the batting-order walk, possible parse gap.`);
    }
    prevInning = inning;
  }
  return atBats;
}
